// src/components/Products/ProductList.jsx
import { useConfig } from "../../contexts/ConfigContext";
import { useAppState } from "../../contexts/AppStateContext";
import LazyImage from '../LazyImage';
import { formattedPrice } from '../Cart/util';
import { getProductImageUrl } from '../../utils/menu/productUtils';
import StrainIcon from './StrainIcon';
import CartPlusIcon from '../Icons/CartPlusIcon.jsx';
import VariantSelect from './VariantSelect';
import VariantBox from './VariantBox';
import SpecialOffer from './SpecialOffer';
import usePricingType from '../../hooks/usePricingType.js';
import EnablePopupWrapper from './Card/EnablePopupWrapper.jsx';

const ProductList = ({
    product,
    prioritySpecialObject,
    allSpecials = null,
    showMultipleSpecials = false,
    singleProductUrl,
    selectedVariant,
    setSelectedVariant, // Retained if direct selection is still part of UI
    handleAddItem,
    variables,
    errors,
    menuSlug,
    handleVariantBoxSelection,
    handleVariantSelection,
    isFirstProduct = false,
}) => {
    const { retailerId } = useAppState();
    const {
        styleOptions,
        themeConfig,
    } = useConfig();
    const [pricingType] = usePricingType(retailerId);
    const hasMultipleVariants = product.variants.length > 1;

    const shouldRenderPriceAndCart =
        !themeConfig?.productVariantsTheme ||
        (!hasMultipleVariants && themeConfig.productVariantsTheme.toLowerCase() === 'boxes') ||
        (!hasMultipleVariants && themeConfig.productVariantsTheme.toLowerCase() === 'boxes-slider') ||
        themeConfig.productVariantsTheme.toLowerCase() === 'dropdown';

    const renderProductImage = () => (
        <EnablePopupWrapper
            product={product}
            productUrl={singleProductUrl}
            classNames={'headless-product-list-image-link'}
        >
            <LazyImage
                classnames='headless-product-list-image'
                src={`${getProductImageUrl(product)}?w=150&fm=webp`}
                alt={`${product.name} product image`}
                fetchPriority={isFirstProduct ? "high" : undefined}
            />
        </EnablePopupWrapper>
    );

    const renderProductInfo = () => (
        <div className='headless-product-list-info'>
            {/* Render specials - either multiple or single */}
            <SpecialOffer
                specialData={prioritySpecialObject}
                allSpecials={allSpecials}
                multiSpecial={showMultipleSpecials}
                menuSlug={menuSlug}
            />

            <h3 className='headless-product-title'>
                <EnablePopupWrapper
                    product={product}
                    productUrl={singleProductUrl}
                >
                    {product.name}
                </EnablePopupWrapper>
            </h3>

            {product.brand ? (
                <div className='headless-breadcrumb headless-product-brands'>
                    {product.brand.name}
                </div>
            ) : null}

            <div className='headless-list-info-details'>
                <div className='headless-list-category'>
                    {product.category}
                </div>

                {product.strainType ? (
                    <div className='headless-product-strain headless-list-strain'>
                        <StrainIcon
                            strainType={product.strainType}
                        />
                    </div>
                ) : null}

                {product.potencyThc.formatted || (product.variants.length === 1 && selectedVariant && selectedVariant.option) ? (
                    <>
                        {product.variants.length === 1 && selectedVariant.option && selectedVariant.option !== 'N/A' && (
                            <div className='headless-product-list-weight headless-product-list-weight-mobile'>
                                {selectedVariant.option}
                            </div>
                        )}
                        {product.potencyThc.formatted && (
                            <div className='headless-product-list-potency'>
                                THC {product.potencyThc.formatted}
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );

    const renderVariantSelector = () => {
        // No changes needed here if VariantBox and VariantSelect don't directly depend on specialId
        // They operate on variants and pricing types.
        if (!hasMultipleVariants) return null;

        if (themeConfig?.productVariantsTheme === 'boxes' || themeConfig?.productVariantsTheme === 'boxes-slider') {
            return (
                <div className='headless-product-list-variant-selector headless-product-list-boxes'>
                    <VariantBox
                        variants={product.variants}
                        // setSelectedVariant={setSelectedVariant} // VariantBox might directly call handleVariantBoxSelection
                        // handleAddItem={handleAddItem} // VariantBox might directly call handleVariantBoxSelection which includes add item
                        pricingType={pricingType}
                        // variables={variables} // VariantBox might construct its own variables for handleVariantBoxSelection
                        handleVariantBoxSelection={handleVariantBoxSelection}
                        boxType='list'
                        theme={themeConfig?.productVariantsTheme}
                        selectedVariantProp={selectedVariant} // Pass selectedVariant to highlight the active one
                    />
                </div>
            );
        }

        return (
            <div className='headless-product-list-variant-selector'>
                <VariantSelect
                    product={product}
                    // setSelectedVariant={setSelectedVariant} // VariantSelect calls handleVariantSelection
                    handleVariantSelection={handleVariantSelection}
                    selectedOptionId={selectedVariant?.id} // Pass selected variant's ID for default selection
                />
            </div>
        );
    };

    const renderWeightInfo = () => {
        if (!selectedVariant) return null; // Guard against undefined selectedVariant

        const shouldShowWeight = (
            (product.variants.length <= 1 && product.category === 'FLOWER') ||
            product.category === 'VAPORIZERS' ||
            (product.category === 'PRE_ROLLS' && selectedVariant.option && selectedVariant.option !== 'N/A')
        );

        if (!shouldShowWeight) return null;

        return (
            <div className='headless-product-list-weight headless-product-list-weight-desktop'>
                {selectedVariant.option}
            </div>
        );
    };

    const renderAddToCartButton = () => {
        if (!selectedVariant || !pricingType) return null; // Guard against undefined values

        const buttonClass = styleOptions?.buttonClass
            ? `${styleOptions.buttonClass} btn-sm`
            : 'headless-button headless-button-sm headless-cart-button';

        const standardPrice = selectedVariant[pricingType.standard];
        const specialPrice = selectedVariant[pricingType.special];
        const hasDiscountPrice = specialPrice && standardPrice && specialPrice < standardPrice;

        return (
            <button
                className={buttonClass}
                onClick={() => handleAddItem.mutate(variables)}
                disabled={handleAddItem.isLoading} // Optional: disable button while adding
            >
                {hasDiscountPrice ? (
                    <>
                        <span>
                            <s className='headless-cart-item-price__strikethrough'>
                                {formattedPrice(standardPrice)}
                            </s>{' '}
                        </span>
                        <span className='headless-pricing-type-special'>
                            <strong>
                                {formattedPrice(specialPrice)}
                            </strong>
                        </span>
                    </>
                ) : (
                    standardPrice !== undefined && ( // Ensure standardPrice is defined before formatting
                        <span className='headless-list-button-price'>
                            <strong>
                                {formattedPrice(standardPrice)}
                            </strong>
                        </span>
                    )
                )}
                <span className='headless-list-button-price-sep'>
                    |
                </span>
                <span className='list-cart-mobile'>
                    <CartPlusIcon />
                </span>
                <span className='list-cart-desktop'>
                    Add to Cart
                </span>
            </button>
        );
    };

    return (
        <>
            <div
                className='headless-product-list-item'
                data-product-id={product.id}
            >
                {errors && errors.length > 0 && // Check if errors is defined
                    errors.map((error, i) => (
                        <div className='headless-error-message' key={i}>
                            <code>{typeof error === 'object' ? JSON.stringify(error) : error}</code>
                        </div>
                    ))}

                <div className='headless-product-content'>
                    <div className='headless-product-list-image-container headless-product-list-image-desktop'>
                        {renderProductImage()}
                    </div>
                    <div className='headless-product-list-content'>
                        {renderProductInfo()}
                        {renderWeightInfo()}
                        <div className='headless-product-list-variantcart'>
                            {themeConfig?.productVariantsTheme === 'boxes' || themeConfig?.productVariantsTheme === 'boxes-slider' ? (
                                <div className='variant-selector-boxes-desktop'>
                                    {renderVariantSelector()}
                                </div>
                            ) : (
                                renderVariantSelector()
                            )}
                            {shouldRenderPriceAndCart && (
                                <div className='headless-add-to-cart headless-add-to-cart-mobile'>
                                    {renderAddToCartButton()}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='headless-product-list-image-container headless-product-list-image-mobile'>
                        {renderProductImage()}
                    </div>
                    {(themeConfig?.productVariantsTheme === 'boxes' || themeConfig?.productVariantsTheme === 'boxes-slider') && (
                        <div className='variant-selector-boxes-mobile'>
                            {renderVariantSelector()}
                        </div>
                    )}
                    {shouldRenderPriceAndCart && (
                        <div className='headless-add-to-cart headless-add-to-cart-desktop'>
                            {renderAddToCartButton()}
                        </div>
                    )}
                </div>
            </div>
            <div className='headless-product-list-hr'></div>
        </>
    );
};

export default ProductList;
